/**
 * Progress Tracker Utility
 * Tracks viewed/completed items using localStorage
 */

const STORAGE_KEY_VIEWED = 'knowledge_base_viewed_items';
const STORAGE_KEY_RECENT = 'knowledge_base_recent_items';
const MAX_RECENT_ITEMS = 10;

/**
 * Mark an item as viewed
 * @param {string} nodeId - The node ID
 * @param {string} label - The node label (optional, for display)
 */
export function markAsViewed(nodeId, label = '') {
  if (!nodeId) return;
  
  const viewed = getViewedItems();
  if (!viewed.includes(nodeId)) {
    viewed.push(nodeId);
    localStorage.setItem(STORAGE_KEY_VIEWED, JSON.stringify(viewed));
  }
  
  // Update recently viewed
  updateRecentlyViewed(nodeId, label);
}

/**
 * Get all viewed items
 * @returns {Array} Array of node IDs
 */
export function getViewedItems() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_VIEWED);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading viewed items:', error);
    return [];
  }
}

/**
 * Check if an item has been viewed
 * @param {string} nodeId - The node ID
 * @returns {boolean} True if viewed
 */
export function isViewed(nodeId) {
  const viewed = getViewedItems();
  return viewed.includes(nodeId);
}

/**
 * Update recently viewed items
 * @param {string} nodeId - The node ID
 * @param {string} label - The node label
 */
function updateRecentlyViewed(nodeId, label) {
  try {
    const recent = getRecentlyViewed();
    
    // Remove if already exists
    const filtered = recent.filter(item => item.id !== nodeId);
    
    // Add to beginning
    filtered.unshift({ id: nodeId, label, timestamp: Date.now() });
    
    // Keep only last MAX_RECENT_ITEMS
    const trimmed = filtered.slice(0, MAX_RECENT_ITEMS);
    
    localStorage.setItem(STORAGE_KEY_RECENT, JSON.stringify(trimmed));
  } catch (error) {
    console.error('Error updating recently viewed:', error);
  }
}

/**
 * Get recently viewed items
 * @returns {Array} Array of { id, label, timestamp }
 */
export function getRecentlyViewed() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_RECENT);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading recently viewed:', error);
    return [];
  }
}

/**
 * Clear all progress data
 */
export function clearProgress() {
  localStorage.removeItem(STORAGE_KEY_VIEWED);
  localStorage.removeItem(STORAGE_KEY_RECENT);
}

/**
 * Get progress statistics
 * @param {Object} tree - The tree structure
 * @param {string} rootId - The root node ID
 * @returns {Object} { total, viewed, percentage }
 */
export function getProgressStats(tree, rootId = 'start') {
  if (!tree || !tree[rootId]) {
    return { total: 0, viewed: 0, percentage: 0 };
  }
  
  const allNodes = getAllNodesWithPages(tree, rootId);
  const viewed = getViewedItems();
  const viewedCount = allNodes.filter(nodeId => viewed.includes(nodeId)).length;
  
  return {
    total: allNodes.length,
    viewed: viewedCount,
    percentage: allNodes.length > 0 ? Math.round((viewedCount / allNodes.length) * 100) : 0,
  };
}

/**
 * Get all nodes that have a page (are viewable)
 * @param {Object} tree - The tree structure
 * @param {string} nodeId - Starting node ID
 * @returns {Array} Array of node IDs with pages
 */
function getAllNodesWithPages(tree, nodeId) {
  const nodes = [];
  const node = tree[nodeId];
  
  if (node) {
    // Only include nodes that have a page property
    if (node.page) {
      nodes.push(nodeId);
    }
    
    // Recursively get children
    if (node.children && node.children.length > 0) {
      node.children.forEach((childId) => {
        nodes.push(...getAllNodesWithPages(tree, childId));
      });
    }
  }
  
  return nodes;
}

export default {
  markAsViewed,
  getViewedItems,
  isViewed,
  getRecentlyViewed,
  clearProgress,
  getProgressStats,
};

